import React, { useEffect, useState } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Menu from '@mui/material/Menu'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Typography from '@/v2/components/common/Typography'
import IconButton from '@/v2/components/common/IconButton'
import CheckboxField from '@/v2/components/common/CheckboxField'
import MenuButton from '@/v2/components/common/MenuButton'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import useModals from '@/v2/hooks/useModals'
import { useTreatments } from '@/v2/hooks/treatments/useTreatments'
import { nowOnClinicTimezone } from '@/v2/utils/convert'
import AppointmentInfo from './AppointmentInfo'
import TreatmentNote from './TreatmentNote'
import RescheduleEvent from './RescheduleEvent'

const AppointmentsTab = () => {
  const queryClient = useQueryClient()
  const { goTo } = useModals()
  const { id } = useParams()
  const location = useLocation()
  const seatAppointmentId = location.state?.seatAppointmentId
  const { patient, onAddNote } = usePatientContext()
  const { treatments, isLoading, isError } = useTreatments(id)
  const { onOpenSnackbar } = useSnackbarContext()

  const [showReschedules, setShowReschedules] = useState(true)
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const filterOpen = Boolean(filterAnchorEl)

  const options = [
    {
      label: 'Add Appointment',
      onClick: () =>
        goTo('/v2/event/new', {
          initialValues: {
            start: nowOnClinicTimezone(),
            patient,
            onCreate: () => {
              queryClient.refetchQueries({
                queryKey: ['PatientAppointments', id],
              })
              queryClient.refetchQueries({
                queryKey: ['Patient', id],
              })
              queryClient.refetchQueries({
                queryKey: ['Treatments', `${id}`],
              })
            },
          },
        }),
    },
    {
      label: 'Add Yellow Note',
      onClick: () =>
        goTo(`/v2/patient/${id}/notes/new/yellow`, {
          onSave: note => onAddNote(note),
        }),
    },
  ]

  useEffect(() => {
    if (!isLoading && !!isError)
      onOpenSnackbar("It wasn't possible to load the patient notes.", 'error')
  }, [isLoading, isError, onOpenSnackbar])

  if (isLoading)
    return (
      <Typography
        variant="h5"
        fontWeight="300"
        sx={{ textAlign: 'center', marginTop: '40px' }}
      >
        Loading...
      </Typography>
    )

  const visibleTreatments = showReschedules
    ? treatments
    : treatments.filter(item => item.type !== 'reschedule')

  return (
    <>
      <Box display="flex" flexDirection="row-reverse" alignItems="center">
        <MenuButton options={options} aria-label="More" title="More">
          Add New
        </MenuButton>
        <IconButton
          aria-label="Treatment options"
          aria-haspopup="true"
          aria-controls={filterOpen ? 'treatment-options-menu' : undefined}
          aria-expanded={filterOpen ? 'true' : undefined}
          title="Treatment options"
          bordercolor="transparent"
          onClick={event => setFilterAnchorEl(event.currentTarget)}
          sx={{ mr: 1 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          id="treatment-options-menu"
          anchorEl={filterAnchorEl}
          open={filterOpen}
          onClose={() => setFilterAnchorEl(null)}
          MenuListProps={{ 'aria-labelledby': 'Treatment options' }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box px={2} py={1}>
            <CheckboxField
              label="Show rescheduled appointments"
              checked={showReschedules}
              onChange={e => setShowReschedules(e.target.checked)}
            />
          </Box>
        </Menu>
      </Box>
      {visibleTreatments.map(item => {
        if (item.type === 'reschedule') {
          return <RescheduleEvent key={`reschedule-${item.id}`} event={item} />
        }
        if (item.classification) {
          return <TreatmentNote key={item.id} note={item} />
        }
        return (
          <AppointmentInfo
            key={item.id}
            appointment={item}
            openAddNote={parseInt(seatAppointmentId, 10) === item.id}
          />
        )
      })}
      {visibleTreatments.length === 0 && (
        <Typography
          variant="h5"
          fontWeight="300"
          sx={{ textAlign: 'center', marginTop: '40px' }}
        >
          No previous data
        </Typography>
      )}
    </>
  )
}

export default AppointmentsTab
